/**
 * Click-to-edit free text, for the status notes on the category page.
 *
 * Renders as plain text until clicked, then becomes an input that saves on
 * blur or Enter and cancels on Escape. A permanently visible input for every
 * subcategory and every item would turn a browsing page into a form; a value
 * that looks like text and edits like text does not.
 *
 * When empty it shows a quiet "add status" affordance rather than nothing at
 * all -- an invisible control is one nobody discovers.
 */

import { useEffect, useRef, useState } from 'react'

interface InlineStatusProps {
  value: string | null
  onSave: (next: string | null) => void | Promise<void>
  placeholder?: string
  /** Text colour of the surface behind it, when that surface is tinted. */
  tintText?: string
  /** Smaller type for item rows, which sit under a subcategory heading. */
  size?: 'sm' | 'xs'
  label: string
}

export function InlineStatus({
  value,
  onSave,
  placeholder = 'add status',
  tintText,
  size = 'sm',
  label,
}: InlineStatusProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  // Keep the draft in step when the value changes from elsewhere (a reload
  // after saving, or an edit made on another device).
  useEffect(() => {
    if (!editing) setDraft(value ?? '')
  }, [value, editing])

  useEffect(() => {
    if (editing) input.current?.focus()
  }, [editing])

  const text = size === 'xs' ? 'text-xs' : 'text-sm'

  async function commit() {
    setEditing(false)
    const next = draft.trim()
    if (next === (value ?? '')) return
    setBusy(true)
    try {
      // Blank clears it; the API normalises '' to null so "no status" has one
      // representation and the display simply renders nothing.
      await onSave(next || null)
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <input
        ref={input}
        className={`field w-full max-w-xs px-2 py-0.5 ${text}`}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
          if (event.key === 'Escape') {
            setDraft(value ?? '')
            setEditing(false)
          }
        }}
        maxLength={200}
        aria-label={label}
        placeholder={placeholder}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={`${label}${value ? `: ${value}` : ' (empty)'}`}
      title="Click to edit"
      style={tintText ? { color: tintText, opacity: value ? 0.95 : 0.6 } : undefined}
      className={[
        text,
        'max-w-full truncate rounded-sm px-1 text-left transition-colors',
        tintText ? 'hover:bg-black/10' : value ? 'text-ink-2 hover:bg-navy-soft' : 'text-ink-3 hover:bg-navy-soft',
        busy ? 'opacity-50' : '',
      ].join(' ')}
    >
      {value || (
        <span className="italic">
          <span aria-hidden>+ </span>
          {placeholder}
        </span>
      )}
    </button>
  )
}
